import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectLastChanged, selectLastChangedBy } from '../../features/claims/claimSlice';
import { selectUserRoles } from '../../features/auth/authSlice';
import { getRoleBasedClaimsRoute } from '../../utils/navigationUtils';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import OmButton from '../ui/OmButton';
import OmBadge from '../ui/OmBadge';
import OmHeader from '../ui/OmHeader';

const ClaimDetailHeader = ({ 
  claim, 
  getClaimTypeLabel, 
  getStatusBadgeColor, 
  canUpdateStatus, 
  getAvailableStatuses, 
  userRoles, 
  onStatusUpdate, 
  onDocumentUpload,
  onRefresh // Add onRefresh prop
}) => {
  const hasUploadRole = userRoles.includes('HR') || userRoles.includes('Broker') || userRoles.includes('CRM') || userRoles.includes('Admin');
  const lastChanged = useSelector(selectLastChanged);
  const lastChangedBy = useSelector(selectLastChangedBy);
  const currentUserRoles = useSelector(selectUserRoles);
  
  // Check if claim status allows document uploads
  const finalizedStatuses = ['APPROVED', 'REJECTED', 'PAID', 'CLOSED'];
  const isClaimFinalized = finalizedStatuses.includes(claim?.status);
  const canUpload = hasUploadRole && !isClaimFinalized;

  // Helper function to map claim status to OmBadge colors
  const getStatusBadgeVariantAndColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUBMITTED':
        return { variant: 'default', color: 'primary' };
      case 'UNDER_REVIEW':
        return { variant: 'default', color: 'warning' };
      case 'RECOMMENDED_FOR_APPROVAL':
        return { variant: 'default', color: 'secondary' };
      case 'APPROVED':
        return { variant: 'default', color: 'success' };
      case 'REJECTED':
        return { variant: 'default', color: 'danger' };
      case 'MISSING_DOCUMENTS':
        return { variant: 'dot', color: 'warning' };
      case 'PAYMENT_PENDING':
        return { variant: 'default', color: 'warning' };
      case 'PAID':
        return { variant: 'default', color: 'success' };
      case 'CLOSED':
        return { variant: 'default', color: 'neutral' };
      default:
        return { variant: 'default', color: 'neutral' };
    }
  };

  return (
    <div className="mb-8 pb-6 border-b border-steel/10">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center space-x-2 text-sm text-steel mb-4">
        <Link to={getRoleBasedClaimsRoute(currentUserRoles)} className="hover:text-fresh-leaf transition-colors duration-200 font-medium">Claims</Link>
        <span>/</span>
        <span className="font-medium">Claim #{claim.id}</span>
      </div>

      {/* Main Content Container */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex-1">
          {/* OmHeader - Main title and subtitle */}
          <OmHeader
            header={`${getClaimTypeLabel(claim.type)} - #${claim.id}`}
            subHeader={`Submitted: ${formatDate(claim.submittedAt)}`}
            type="page-header"
            className="mb-4"
          />
          
          {/* Status and Metadata */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <OmBadge
                variant={getStatusBadgeVariantAndColor(claim.status).variant}
                color={getStatusBadgeVariantAndColor(claim.status).color}
                size="large"
                className="font-medium"
              >
                {claim.status?.replace('_', ' ') || 'Unknown'}
              </OmBadge>
              {claim.status === 'MISSING_DOCUMENTS' && (
                <OmBadge
                  variant="dot"
                  color="warning"
                  size="small"
                  className="ml-1"
                >
                  Action Required
                </OmBadge>
              )}
            </div>
            {lastChanged && (
              <span className="text-sm text-steel">
                Last changed: {formatDateTime(lastChanged)}
                {lastChangedBy && ` by ${lastChangedBy.username}`}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Upload Documents button removed - functionality hidden from UI */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimDetailHeader;
